import moment from "moment";

// Printed documents, in the layout of the clinical OX.ar print: a title block
// with the patient, a body of plain tables, and a footer with the print time.
// The page is built as HTML and printed from its own window, so the portal's
// screen styles never leak into the paper copy.

export const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function patientLine() {
  try {
    const u = JSON.parse(localStorage.getItem("UData"));
    if (!u) return "";
    const dob = u.birthday ? moment(u.birthday).format("DD-MMM-YYYY") : "";
    return [
      [u.firstName, u.lastName].filter(Boolean).join(" "),
      dob && `DOB ${dob}${u.age !== undefined && u.age !== null ? ` (${u.age})` : ""}`,
      u.gendercodeName,
    ].filter(Boolean).join(" \u00b7 ");
  } catch {
    return "";
  }
}

const STYLE = `
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font: 11px/1.4 "Segoe UI", Arial, sans-serif; color: #1c2733; margin: 0; }
  header.doc { border-bottom: 2px solid #2f7f72; padding-bottom: 8px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: flex-end; }
  header.doc h1 { font-size: 18px; margin: 0; }
  header.doc .who { color: #5f6f80; margin-top: 2px; }
  header.doc .brand { font-weight: 700; color: #2f7f72; letter-spacing: .04em; }
  h2 { font-size: 12.5px; margin: 16px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #e3e8ee; text-transform: uppercase; letter-spacing: .05em; color: #256a5f; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #d6dde5; padding: 3px 6px; text-align: left; vertical-align: top; }
  th { background: #f1f5f8; font-weight: 600; }
  td.n, th.n { text-align: right; }
  tr { break-inside: avoid; }
  .flag { color: #b3261e; font-weight: 700; }
  .muted { color: #5f6f80; }
  .kv { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 14px; margin-bottom: 8px; }
  .kv div span { display: block; font-size: 9px; text-transform: uppercase; color: #5f6f80; letter-spacing: .04em; }
  footer.doc { margin-top: 18px; padding-top: 6px; border-top: 1px solid #e3e8ee; color: #5f6f80; font-size: 9.5px; display: flex; justify-content: space-between; }
  .wide { font-size: 9px; }
  .wide th, .wide td { padding: 2px 4px; }
`;

// Opens the document in its own window and prints it. Returns false when the
// browser blocks the pop-up, so the caller can say so.
export function printHtml({ title, subtitle = "", bodyHtml, landscape = false, extraCss = "" }) {
  const w = window.open("", "_blank", "width=1000,height=800");
  if (!w) return false;
  const when = moment().format("DD-MMM-YYYY HH:mm");
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${STYLE}${landscape ? "@page { size: A4 landscape; margin: 9mm 10mm; }" : ""}${extraCss}</style></head><body>
    <header class="doc">
      <div><h1>${esc(title)}</h1><div class="who">${esc(patientLine())}${subtitle ? ` &middot; ${esc(subtitle)}` : ""}</div></div>
      <div class="brand">OX.assisted reproduction</div>
    </header>
    ${bodyHtml}
    <footer class="doc"><span>Printed ${esc(when)} from the patient portal</span><span>${esc(localStorage.getItem("ClinicName") || "")}</span></footer>
  </body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch { /* the user can print from the window */ } }, 350);
  return true;
}

export function tableHtml(headers, rows, { cls = "", numeric = [] } = {}) {
  const head = headers.map((h, i) => `<th class="${numeric.includes(i) ? "n" : ""}">${esc(h)}</th>`).join("");
  const body = rows
    .map((r) => `<tr>${r.map((c, i) => `<td class="${numeric.includes(i) ? "n" : ""}">${c && c.html !== undefined ? c.html : esc(c)}</td>`).join("")}</tr>`)
    .join("");
  return `<table class="${cls}"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

// iCalendar text for one appointment, for "Add to my calendar".
export function icsFor({ title, start, end, location = "", description = "" }) {
  const fmt = (d) => moment(d).utc().format("YYYYMMDD[T]HHmmss[Z]");
  const clean = (t) => String(t ?? "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//OX.ar Patient Portal//EN",
    "BEGIN:VEVENT",
    `UID:${fmt(start)}-${Math.abs(String(title).split("").reduce((a, c) => a * 31 + c.charCodeAt(0), 7))}@oxar-portal`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end || moment(start).add(30, "minutes"))}`,
    `SUMMARY:${clean(title)}`,
    `LOCATION:${clean(location)}`,
    `DESCRIPTION:${clean(description)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadText(filename, text, type = "text/calendar") {
  const blob = new Blob([text], { type: `${type};charset=utf-8` });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    ta.remove();
    return ok;
  }
}
